// noiseFilterHooks.js - Custom hooks for noise filter functionality
import { useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  queueNoiseFilter,
  reset,
  updateNoiseFilter,
  setTimeNoiseFilter,
} from "./index";
import { getOriginalTime } from "../originalTime";

/**
 * Hook for noise filter management functionality
 */
export const useNoiseFilter = () => {
  const dispatch = useDispatch();
  const noiseFilter = useSelector((state) => state.noiseFilter);

  // Queue a noise filter request
  const handleQueueRequest = useCallback(
    (caseId, clipId, noiseFilterRequest) => {
      return dispatch(queueNoiseFilter({ caseId, clipId, noiseFilterRequest }));
    },
    [dispatch],
  );

  // Reset noise filter state
  const handleReset = useCallback(() => {
    return dispatch(reset());
  }, [dispatch]);

  // Set time for the current clip
  const handleSetTime = useCallback(
    (clipTime, clip) => {
      let originalTime = clipTime;

      if (clip?.timeMappings) {
        originalTime = getOriginalTime(clip.timeMappings, clipTime);
      }

      return dispatch(setTimeNoiseFilter({ clipTime, originalTime }));
    },
    [dispatch],
  );

  // Update noise filter parameters
  const handleUpdate = useCallback(
    (updates) => {
      return dispatch(updateNoiseFilter(updates));
    },
    [dispatch],
  );

  return {
    // State
    noiseFilterStatus: noiseFilter.noiseFilterStatus,
    selectedFile: noiseFilter.selectedFile,
    filtering: noiseFilter.filtering,
    filtered: noiseFilter.filtered,
    guid: noiseFilter.guid,
    clipId: noiseFilter.clipId,
    commitId: noiseFilter.commitId,
    guidLoading: noiseFilter.guidLoading,
    guidLoaded: noiseFilter.guidLoaded,
    noiseStart: noiseFilter.noiseStart,
    noiseEnd: noiseFilter.noiseEnd,
    fftLength: noiseFilter.fftLength,
    noiseCancelRatio: noiseFilter.noiseCancelRatio,
    fdMaskAveraging: noiseFilter.fdMaskAveraging,
    tdMaskAveraging: noiseFilter.tdMaskAveraging,
    nStdThreshold: noiseFilter.nStdThreshold,

    clipTime: noiseFilter.clipTime,
    originalTime: noiseFilter.originalTime,
    waveOpacity: noiseFilter.waveOpacity,
    regionSelected: noiseFilter.regionSelected,
    region: noiseFilter.region,

    // Actions
    queueNoiseFilter: handleQueueRequest,
    reset: handleReset,
    setTimeNoiseFilter: handleSetTime,
    updateNoiseFilter: handleUpdate,
  };
};
